import {
  lazy,
  Suspense,
  useLayoutEffect,
  useMemo,
  type ComponentType,
  type ReactNode,
} from "react";
import {
  createBrowserRouter,
  isRouteErrorResponse,
  Navigate,
  useLocation,
  useRouteError,
} from "react-router-dom";
import { MarketingLayout } from "@/shared/layout/MarketingLayout";
import { AppLayout } from "@/shared/layout/AppLayout";
import { SettingsLayout } from "@/features/settings/SettingsLayout";
import { getLastAppRoute } from "@/shared/navigation/app-route-memory";
import { VerifyLinkRootShell } from "@/shared/auth/VerifyLinkOverlay";
import {
  isDedicatedPasswordResetOrigin,
  passwordResetAuthExitDestination,
} from "@/shared/auth/reset-origin-navigation.mjs";
import { RouteAccessibility } from "@/shared/a11y/RouteAccessibility";
import { RouteAnalytics } from "@/shared/analytics/RouteAnalytics";
import {
  captureInitialGoogleCalendarOAuthReturn,
  GOOGLE_CALENDAR_OAUTH_COMPLETE_PATH,
} from "@/shared/calendar/calendar-oauth-redirect";
import { publicWebCopyForLang } from "@/shared/i18n/public-web-copy";
import { useMarketingExperience } from "@/shared/i18n/use-product-locale";
import { DocumentLanguage } from "@/shared/i18n/DocumentLanguage";
import { AppStartPage } from "./AppStartPage";
import { RequireAuth } from "./RequireAuth";
import {
  claimLazyRouteReload,
  clearLazyRouteReload,
  isLazyRouteLoadError,
} from "./lazy-route-recovery.mjs";

type LazyRouteModule = { default: ComponentType };

function routeRecoveryStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function lazyRoute(routeKey: string, load: () => Promise<LazyRouteModule>) {
  return lazy(async () => {
    const storage = routeRecoveryStorage();
    try {
      const module = await load();
      clearLazyRouteReload(storage, routeKey);
      return module;
    } catch (error) {
      if (isLazyRouteLoadError(error) && claimLazyRouteReload(storage, routeKey)) {
        window.location.reload();
        return new Promise<LazyRouteModule>(() => {});
      }
      throw error;
    }
  });
}

const PricingPage = lazyRoute("pricing", () =>
  import("@/features/marketing/PricingPage").then((module) => ({ default: module.PricingPage })),
);
const FaqPage = lazyRoute("faq", () =>
  import("@/features/marketing/FaqPage").then((module) => ({ default: module.FaqPage })),
);
const ReferralLandingPage = lazyRoute("referral", () =>
  import("@/features/marketing/ReferralLandingPage").then((module) => ({
    default: module.ReferralLandingPage,
  })),
);
const OnboardingPage = lazyRoute("onboarding", () =>
  import("@/features/onboarding/OnboardingPage").then((module) => ({
    default: module.OnboardingPage,
  })),
);
const SignInPage = lazyRoute("sign-in", () =>
  import("@/features/auth/SignInPage").then((module) => ({ default: module.SignInPage })),
);
const SignUpPage = lazyRoute("sign-up", () =>
  import("@/features/auth/SignUpPage").then((module) => ({ default: module.SignUpPage })),
);
const SignUpVerificationPage = lazyRoute("sign-up-verification", () =>
  import("@/features/auth/SignUpVerificationPage").then((module) => ({
    default: module.SignUpVerificationPage,
  })),
);
const ForgotPasswordPage = lazyRoute("forgot-password", () =>
  import("@/features/auth/ForgotPasswordPage").then((module) => ({
    default: module.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazyRoute("reset-password", () =>
  import("@/features/auth/ResetPasswordPage").then((module) => ({
    default: module.ResetPasswordPage,
  })),
);
const VerifyLinkPage = lazyRoute("verify-link", () =>
  import("@/features/auth/VerifyLinkPage").then((module) => ({ default: module.VerifyLinkPage })),
);
const CalendarOAuthCompletePage = lazyRoute("calendar-oauth-complete", () =>
  import("@/features/calendar/CalendarOAuthCompletePage").then((module) => ({
    default: module.CalendarOAuthCompletePage,
  })),
);
const HomePage = lazyRoute("home", () =>
  import("@/features/home/HomePage").then((module) => ({ default: module.HomePage })),
);
const BriefPage = lazyRoute("brief", () =>
  import("@/features/brief/BriefPage").then((module) => ({ default: module.BriefPage })),
);
const CalendarPage = lazyRoute("calendar", () =>
  import("@/features/calendar/CalendarPage").then((module) => ({ default: module.CalendarPage })),
);
const NewsPage = lazyRoute("news", () =>
  import("@/features/news/NewsPage").then((module) => ({ default: module.NewsPage })),
);
const MarketsPage = lazyRoute("markets", () =>
  import("@/features/markets/MarketsPage").then((module) => ({ default: module.MarketsPage })),
);
const TasksPage = lazyRoute("tasks", () =>
  import("@/features/tasks/TasksPage").then((module) => ({ default: module.TasksPage })),
);
const SavedPage = lazyRoute("saved", () =>
  import("@/features/saved/SavedPage").then((module) => ({ default: module.SavedPage })),
);
const ReaderPage = lazyRoute("reader", () =>
  import("@/features/reader/ReaderPage").then((module) => ({ default: module.ReaderPage })),
);
const ProfilePage = lazyRoute("profile", () =>
  import("@/features/settings/ProfilePage").then((module) => ({ default: module.ProfilePage })),
);
const SettingsOverviewPage = lazyRoute("settings", () =>
  import("@/features/settings/SettingsOverviewPage").then((module) => ({
    default: module.SettingsOverviewPage,
  })),
);
const SettingsTourPage = lazyRoute("settings-tour", () =>
  import("@/features/settings/SettingsTourPage").then((module) => ({ default: module.SettingsTourPage })),
);
const PreferencesPage = lazyRoute("preferences", () =>
  import("@/features/settings/PreferencesPage").then((module) => ({
    default: module.PreferencesPage,
  })),
);
const NotificationsPage = lazyRoute("notifications", () =>
  import("@/features/settings/NotificationsPage").then((module) => ({
    default: module.NotificationsPage,
  })),
);
const SubscriptionPage = lazyRoute("subscription", () =>
  import("@/features/subscription/SubscriptionPage").then((module) => ({
    default: module.SubscriptionPage,
  })),
);
const ReferralsPage = lazyRoute("referrals", () =>
  import("@/features/settings/ReferralsPage").then((module) => ({ default: module.ReferralsPage })),
);
const PrivacyPage = lazyRoute("privacy", () =>
  import("@/features/settings/PrivacyPage").then((module) => ({ default: module.PrivacyPage })),
);
const SupportPage = lazyRoute("support", () =>
  import("@/features/settings/SupportPage").then((module) => ({ default: module.SupportPage })),
);

function RouteFallback() {
  const experience = useMarketingExperience();
  const copy = useMemo(() => publicWebCopyForLang(experience.lang), [experience.lang]);
  return (
    <div className="route-fallback" role="status" aria-live="polite">
      {copy("auth.route.loading")}
    </div>
  );
}

function RouteErrorPage() {
  const experience = useMarketingExperience();
  const copy = useMemo(() => publicWebCopyForLang(experience.lang), [experience.lang]);
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  const staleRoute = isLazyRouteLoadError(error);

  return (
    <>
      <DocumentLanguage />
      <main id="main-content" className="route-error" data-route-focus-target tabIndex={-1}>
        <div className="route-error__card">
          <p>{notFound ? copy("auth.route.notFoundKicker") : copy("auth.route.refreshKicker")}</p>
          <h1>{notFound ? copy("auth.route.notAvailable") : copy("auth.route.reconnect")}</h1>
          <p>{staleRoute ? copy("auth.route.stale") : copy("auth.route.loadFailed")}</p>
          <div className="route-error__actions">
            <button type="button" onClick={() => window.location.reload()}>
              {copy("auth.route.reload")}
            </button>
            <a href="/">{copy("auth.route.home")}</a>
          </div>
        </div>
      </main>
    </>
  );
}

function route(element: ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

function AppIndexRedirect() {
  return <Navigate to={getLastAppRoute()} replace />;
}

function HomeRoute() {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const surface = query.get("surface");
  if (surface !== "dashboard") {
    query.delete("surface");
    const search = query.toString();
    return <Navigate to={{ pathname: "/app/brief", search: search ? `?${search}` : "" }} replace />;
  }
  return route(<HomePage />);
}

function ResetHostRouteBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  const currentOrigin = window.location.origin;
  const isResetHost = isDedicatedPasswordResetOrigin(currentOrigin);
  const isAllowedResetPath =
    location.pathname === "/auth/reset" ||
    location.pathname === "/auth/verify-link" ||
    location.pathname.startsWith("/auth/verify-link/");

  useLayoutEffect(() => {
    if (!isResetHost || isAllowedResetPath) return;
    window.location.replace(passwordResetAuthExitDestination("/auth/sign-in", currentOrigin));
  }, [currentOrigin, isAllowedResetPath, isResetHost]);

  if (isResetHost && !isAllowedResetPath) {
    return <RouteFallback />;
  }
  return children;
}

// Router initialization runs before React mounts AuthProvider or analytics.
// Capture and scrub the fragment here so the opaque completion handle never
// reaches route effects, history memory, or telemetry.
captureInitialGoogleCalendarOAuthReturn();

export const router = createBrowserRouter([
  {
    errorElement: <RouteErrorPage />,
    element: (
      <ResetHostRouteBoundary>
        <DocumentLanguage />
        <RouteAccessibility />
        <RouteAnalytics />
        <VerifyLinkRootShell />
      </ResetHostRouteBoundary>
    ),
    children: [
      {
        element: <MarketingLayout />,
        children: [
          { index: true, element: <AppStartPage /> },
          { path: "onboarding", element: route(<OnboardingPage />) },
          { path: "pricing", element: route(<PricingPage />) },
          { path: "faq", element: route(<FaqPage />) },
          { path: "r/:referralCode", element: route(<ReferralLandingPage />) },
        ],
      },
      { path: "v/:token", element: route(<VerifyLinkPage />) },
      { path: "auth/verify-link/:token", element: route(<VerifyLinkPage />) },
      {
        path: GOOGLE_CALENDAR_OAUTH_COMPLETE_PATH.slice(1),
        element: route(<CalendarOAuthCompletePage />),
      },
      {
        path: "auth",
        element: <MarketingLayout />,
        children: [
          { path: "sign-in", element: route(<SignInPage />) },
          { path: "sign-up", element: route(<SignUpPage />) },
          { path: "sign-up/verify", element: route(<SignUpVerificationPage />) },
          { path: "forgot", element: route(<ForgotPasswordPage />) },
          { path: "reset", element: route(<ResetPasswordPage />) },
        ],
      },
      {
        path: "app",
        element: (
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <AppIndexRedirect /> },
          { path: "home", element: <HomeRoute /> },
          { path: "brief", element: route(<BriefPage />) },
          { path: "calendar", element: route(<CalendarPage />) },
          { path: "news", element: route(<NewsPage />) },
          { path: "markets", element: route(<MarketsPage />) },
          { path: "tasks", element: route(<TasksPage />) },
          { path: "saved", element: route(<SavedPage />) },
          { path: "reader/:payload", element: route(<ReaderPage />) },
          {
            path: "settings",
            element: <SettingsLayout />,
            children: [
              { index: true, element: route(<SettingsOverviewPage />) },
              { path: "profile", element: route(<ProfilePage />) },
              { path: "preferences", element: route(<PreferencesPage />) },
              { path: "notifications", element: route(<NotificationsPage />) },
              { path: "subscription", element: route(<SubscriptionPage />) },
              { path: "referrals", element: route(<ReferralsPage />) },
              { path: "privacy", element: route(<PrivacyPage />) },
              { path: "support", element: route(<SupportPage />) },
              { path: "tour", element: route(<SettingsTourPage />) },
            ],
          },
        ],
      },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);
